import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from "@angular/core";

import { HttpClient } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { map, catchError, Observable } from 'rxjs';

export interface NewsItem {
    title: string;
    link: string;
    pubDate: string;
    source: string;
    description: string;
}

export interface NewsCategory {
    id: string;
    name: string;
    items: NewsItem[];
    loading: boolean;
    error: string | null;
}

@Component({
    selector: 'app-google-news',
    standalone: true,
    imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    MatListModule
],
    templateUrl: './google-news.component.html',
    styleUrls: ['./google-news.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoogleNewsComponent implements OnInit {
    private http = inject(HttpClient);

    categories = signal<NewsCategory[]>([
        { id: 'focus', name: '焦點新聞', items: [], loading: true, error: null },
        { id: 'taiwan', name: '台灣新聞', items: [], loading: true, error: null },
        { id: 'world', name: '國際新聞', items: [], loading: true, error: null },
        { id: 'business', name: '商業新聞', items: [], loading: true, error: null },
        { id: 'tech', name: '科技新聞', items: [], loading: true, error: null },
        { id: 'ai', name: 'AI新聞', items: [], loading: true, error: null }
    ]);

    private rssUrls: { [key: string]: string } = {
        'focus': 'https://news.google.com/rss?hl=zh-TW&gl=TW&ceid=TW:zh-Hant',
        'taiwan': 'https://news.google.com/rss/search?q=台灣&hl=zh-TW&gl=TW&ceid=TW:zh-Hant',
        'world': 'https://news.google.com/rss/search?q=國際&hl=zh-TW&gl=TW&ceid=TW:zh-Hant',
        'business': 'https://news.google.com/rss/search?q=商業&hl=zh-TW&gl=TW&ceid=TW:zh-Hant',
        'tech': 'https://news.google.com/rss/search?q=科技&hl=zh-TW&gl=TW&ceid=TW:zh-Hant',
        'ai': 'https://news.google.com/rss/search?q=AI%20人工智慧&hl=zh-TW&gl=TW&ceid=TW:zh-Hant'
    };

    ngOnInit() {
        this.fetchAllNews();
    }

    refresh() {
        this.fetchAllNews();
    }

    private fetchAllNews() {
        this.categories.update(cats => cats.map(c => ({ ...c, loading: true, error: null, items: [] })));

        const categories = this.categories();

        categories.forEach(category => {
            this.fetchCategoryNews(category.id).subscribe({
                next: (items) => {
                    this.categories.update(cats =>
                        cats.map(c => c.id === category.id ? { ...c, items, loading: false } : c)
                    );
                },
                error: (err) => {
                    console.error(`Error fetching ${category.name}:`, err);
                    this.categories.update(cats =>
                        cats.map(c => c.id === category.id ? { ...c, loading: false, error: '無法載入新聞 (可能是 CORS 限制，請確認環境)' } : c)
                    );
                }
            });
        });
    }

    private fetchCategoryNews(categoryId: string): Observable<NewsItem[]> {
        const targetUrl = this.rssUrls[categoryId];
        // Use allorigins.win as a CORS proxy
        const url = `https://api.allorigins.win/raw?url=${encodeURIComponent(targetUrl)}`;

        return this.http.get(url, { responseType: 'text' }).pipe(
            map(xml => {
                const items = this.parseRSS(xml);

                // Special logic for Taiwan News: Prioritize Central News Agency (中央社)
                if (categoryId === 'taiwan') {
                    return this.prioritizeCNA(items);
                }

                return items.slice(0, 5); // Take top 5
            }),
            catchError(err => {
                console.error('Fetch error:', err);
                // Fallback or retry logic can be added here
                throw err;
            })
        );
    }

    private parseRSS(xmlStr: string): NewsItem[] {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlStr, "text/xml");
        const items = Array.from(xmlDoc.querySelectorAll("item"));

        return items.map(item => {
            const title = item.querySelector("title")?.textContent || "";
            const link = item.querySelector("link")?.textContent || "";
            const pubDate = item.querySelector("pubDate")?.textContent || "";
            const description = item.querySelector("description")?.textContent || "";
            const source = item.querySelector("source")?.textContent || "";

            // Clean description (Google News often puts HTML in description)
            // Extracting plain text might be better, or just using title which is descriptive.
            // Often title is "Headline - Source".

            let derivedSource = source;
            if (!derivedSource && title.includes("-")) {
                derivedSource = title.split("-").pop()?.trim() || "";
            }

            return {
                title,
                link,
                pubDate: new Date(pubDate).toLocaleDateString('zh-TW', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
                source: derivedSource,
                description
            };
        });
    }

    private prioritizeCNA(items: NewsItem[]): NewsItem[] {
        // Separate CNA news and others
        const cnaNews = items.filter(item => item.source.includes('中央社') || item.title.includes('中央社'));
        const otherNews = items.filter(item => !item.source.includes('中央社') && !item.title.includes('中央社'));

        // Combine: CNA first, then others
        const combined = [...cnaNews, ...otherNews];
        return combined.slice(0, 5); // Take top 5 after reordering
    }
}
